import type { SuretyBondFormType } from "@inrev/common";
import { type FC, useLayoutEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
	BOND_FORM_HEADER,
	BOND_FORM_UPLOAD_ONLY_TOOLTIP,
	BondFormSectionHeader,
} from "../../../form/BondFormSectionHeader";
import { FormBondFormSelect } from "../../../form/fields/FormBondFormSelect";
import { FormDatePicker } from "../../../form/fields/FormDatePicker";
import { FormMoney } from "../../../form/fields/FormMoney";
import { FormTextArea } from "../../../form/fields/FormTextArea";
import { Button } from "../../../primitives/Button";
import {
	COMMERCIAL_INFO_FIELDS,
	type SubmissionFormData,
} from "../bondRequestSubmissionFormSchema";
import { StepFieldLabel } from "./StepFieldLabel";

type CommercialInfoStepProps = {
	onBack: () => void;
	onNext: () => void;
};

const EMPTY_TEMPLATES: never[] = [];
// Commercial bonds have no bond form type, so uploads are never classified.
const NO_TYPE_LABELS: Partial<Record<SuretyBondFormType, string>> = {};

export const CommercialInfoStep: FC<CommercialInfoStepProps> = ({ onBack, onNext }) => {
	const stepRef = useRef<HTMLDivElement>(null);
	const { control, trigger } = useFormContext<SubmissionFormData>();
	const bondForm = useWatch({ control, name: "bondForm" });
	const bondFormSelected = (bondForm?.upload?.files.length ?? 0) > 0;

	useLayoutEffect(() => {
		stepRef.current?.focus({ preventScroll: true });
	}, []);

	const handleNext = async () => {
		if (!(await trigger([...COMMERCIAL_INFO_FIELDS]))) return;
		onNext();
	};

	return (
		<div
			ref={stepRef}
			className="flex flex-col gap-5 px-6 pt-5 pb-8 outline-none"
			data-testid="commercial-info-step"
			tabIndex={-1}
		>
			<div className="flex flex-col gap-3">
				<StepFieldLabel required>Bond Amount</StepFieldLabel>
				<FormMoney<SubmissionFormData>
					name="bondAmount"
					control={control}
					testId="bond-amount-input"
					placeholder="0.00"
				/>
			</div>

			<div className="flex flex-col gap-3" data-testid="effective-date-input">
				<StepFieldLabel required>Effective Date</StepFieldLabel>
				<FormDatePicker<SubmissionFormData> name="effectiveDate" control={control} />
			</div>

			<div className="flex flex-col gap-3">
				<StepFieldLabel>Submission Narrative</StepFieldLabel>
				<FormTextArea<SubmissionFormData>
					name="submissionNarrative"
					control={control}
					className="h-[110px]"
					testId="submission-narrative-input"
					placeholder="Include relevant amounts and dates, special requirements, or other context that may help process this request..."
				/>
			</div>

			<div className="flex flex-col gap-3">
				<BondFormSectionHeader
					tooltipId="commercial-bond-form-guidance"
					showTooltip={!bondFormSelected}
					tooltipContent={BOND_FORM_UPLOAD_ONLY_TOOLTIP}
					label={<StepFieldLabel>{BOND_FORM_HEADER}</StepFieldLabel>}
					className="gap-1.5"
					tooltipClassName="text-[16px]"
					tooltipMaxWidth={300}
					condensed={false}
				/>
				<FormBondFormSelect<SubmissionFormData>
					name="bondForm"
					control={control}
					templates={EMPTY_TEMPLATES}
					typeLabels={NO_TYPE_LABELS}
					allowLibrary={false}
					id="bondForm"
					compact
				/>
			</div>

			<div className="flex justify-end gap-2 pt-1">
				<Button color="secondary" onClick={onBack}>
					Back
				</Button>
				<Button color="primary" onClick={handleNext}>
					Next
				</Button>
			</div>
		</div>
	);
};
